/**
 * CaseFileContext
 *
 * Provides global state for the currently selected CaseFile.
 * Persists the selection across page navigation within the app.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { useAuth } from './AuthContext';

type CaseFilePermission = 'owner' | 'editor' | 'logger' | 'viewer';

interface CaseFileData {
  caseFileId: string;
  name: string;
  userPermissions?: CaseFilePermission;
  isLocked?: boolean;
  lockReason?: string;
  dateOfBirth?: string;
  careRecipientEmail?: string;
}

interface ListCaseFilesResponse {
  success: boolean;
  caseFiles: CaseFileData[];
  totalCount: number;
}

interface CaseFileContextType {
  caseFiles: CaseFileData[];
  activeCaseFileId: string | undefined;
  activeCaseFile: CaseFileData | undefined;
  loading: boolean;
  error: string | null;
  setActiveCaseFileId: (id: string) => void;
  refreshCaseFiles: () => Promise<void>;
}

const CaseFileContext = createContext<CaseFileContextType | undefined>(undefined);

interface CaseFileProviderProps {
  children: ReactNode;
}

const STORAGE_KEY = 'hesvara_active_casefile_id';

export function CaseFileProvider({ children }: CaseFileProviderProps): JSX.Element {
  const { user } = useAuth();
  const [caseFiles, setCaseFiles] = useState<CaseFileData[]>([]);
  // Store the raw value from localStorage but don't expose it until validated
  const [activeCaseFileId, setActiveCaseFileIdState] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Persist to localStorage when activeCaseFileId changes
  const setActiveCaseFileId = useCallback((id: string) => {
    setActiveCaseFileIdState(id);
    localStorage.setItem(STORAGE_KEY, id);
  }, []);

  // Fetch CaseFiles when user changes
  const fetchCaseFiles = useCallback(async () => {
    if (!user) {
      setCaseFiles([]);
      setActiveCaseFileIdState(undefined);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const listUserCaseFiles = httpsCallable<Record<string, never>, ListCaseFilesResponse>(
        functions,
        'listUserCaseFiles'
      );
      const result = await listUserCaseFiles({});

      if (result.data.success && result.data.caseFiles) {
        const fetchedCaseFiles = result.data.caseFiles.map((cf) => ({
          caseFileId: cf.caseFileId,
          name: cf.name,
          userPermissions: cf.userPermissions,
          isLocked: cf.isLocked,
          lockReason: cf.lockReason,
          dateOfBirth: cf.dateOfBirth,
          careRecipientEmail: cf.careRecipientEmail,
        }));
        setCaseFiles(fetchedCaseFiles);

        // Validate stored activeCaseFileId or set first CaseFile as active
        const storedId = localStorage.getItem(STORAGE_KEY);
        const isStoredIdValid = fetchedCaseFiles.some((cf) => cf.caseFileId === storedId);

        if (storedId && isStoredIdValid) {
          setActiveCaseFileIdState(storedId);
        } else if (fetchedCaseFiles.length > 0) {
          const firstId = fetchedCaseFiles[0].caseFileId;
          setActiveCaseFileIdState(firstId);
          localStorage.setItem(STORAGE_KEY, firstId);
        } else {
          setActiveCaseFileIdState(undefined);
          localStorage.removeItem(STORAGE_KEY);
        }
      }
    } catch (err) {
      console.error('Error fetching CaseFiles:', err);
      setError(err instanceof Error ? err.message : 'Failed to load CaseFiles');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchCaseFiles();
  }, [fetchCaseFiles]);

  // Clear state on sign out
  useEffect(() => {
    if (!user) {
      setCaseFiles([]);
      setActiveCaseFileIdState(undefined);
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [user]);

  const activeCaseFile = caseFiles.find((cf) => cf.caseFileId === activeCaseFileId);

  const value: CaseFileContextType = {
    caseFiles,
    activeCaseFileId,
    activeCaseFile,
    loading,
    error,
    setActiveCaseFileId,
    refreshCaseFiles: fetchCaseFiles,
  };

  return (
    <CaseFileContext.Provider value={value}>
      {children}
    </CaseFileContext.Provider>
  );
}

export function useCaseFile(): CaseFileContextType {
  const context = useContext(CaseFileContext);
  if (context === undefined) {
    throw new Error('useCaseFile must be used within a CaseFileProvider');
  }
  return context;
}
